// PhotoUpload — reusable file picker + client-side resize + auto-upload.
//
// Used on AgentEdit, PlayerEdit, CoachEdit. Server-side validation is the
// real defense (5 MB cap, image-only, owner-gated) — the client-side resize
// is a UX nicety to avoid uploading 12 MP phone photos. We resize to a
// 1024-px max edge JPEG at q=0.85, which lands well under 5 MB for any
// realistic headshot.
//
// UX: file pick triggers the upload immediately. A local object-URL preview
// shows in the avatar slot during the resize + POST so the user sees
// feedback within a frame, with an "Uploading…" overlay over it. Earlier
// drafts had a two-step "Choose File → Upload" flow but users (rightly)
// expected the preview to mean "saved" — they'd close the page without
// clicking the second button. Auto-upload removes the foot-gun.
//
// `deferred` mode (M5 follow-up — OnboardingCoach):
//   At setup time the owning row (coaches / agents) doesn't exist yet, so
//   /api/upload/photo's requireCoachOwner / requireAgentOwner would 403.
//   In deferred mode we skip the POST entirely: pick → validate → resize →
//   onFilePicked(blob). The parent holds the blob and uploads AFTER the
//   row is created on form submit. `currentPhotoUrl` / `onChange` are
//   irrelevant in this mode because the user navigates away on submit.

import { useId, useRef, useState } from 'react'
import { apiFetch } from '../lib/api'

const DEFAULT_MAX_EDGE_PX = 1024
const JPEG_QUALITY = 0.85
const ACCEPT = 'image/jpeg,image/png,image/webp'

type EntityType = 'agent' | 'player' | 'coach'

interface Props {
  entityType: EntityType
  // Required only for entityType === 'player'. Ignored for agent/coach (the
  // server derives those from the session — see /api/upload/photo).
  entityId?: string
  currentPhotoUrl: string | null
  onChange: (newUrl: string) => void
  shape?: 'circle' | 'square'
  label?: string
  // Deferred mode — used by setup forms where the owning entity row doesn't
  // exist yet. File is resized + previewed locally but NOT uploaded; the
  // parent receives the resized JPEG blob via onFilePicked and is
  // responsible for POSTing it (typically inside its submit handler, after
  // the coach/agent row has been created).
  deferred?: boolean
  onFilePicked?: (resized: Blob | null) => void
  // Optional max edge in pixels for resize. Defaults to 1024.
  maxEdgePx?: number
}

interface UploadResponse {
  photo_url: string
}

async function resizeToJpeg(file: File, maxEdgePx: number): Promise<Blob> {
  const url = URL.createObjectURL(file)
  try {
    const img = await loadImage(url)
    const scale = Math.min(1, maxEdgePx / Math.max(img.width, img.height))
    const width = Math.round(img.width * scale)
    const height = Math.round(img.height * scale)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas_unavailable')
    ctx.drawImage(img, 0, 0, width, height)
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    )
    if (!blob) throw new Error('encode_failed')
    return blob
  } finally {
    URL.revokeObjectURL(url)
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image_decode_failed'))
    img.src = src
  })
}

export default function PhotoUpload({
  entityType,
  entityId,
  currentPhotoUrl,
  onChange,
  shape = 'circle',
  label = 'Profile photo',
  deferred = false,
  onFilePicked,
  maxEdgePx = DEFAULT_MAX_EDGE_PX,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  // previewUrl is a local object-URL shown immediately on file pick. It's
  // replaced by the real server URL via onChange() once the upload settles.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // A11y — bind the visible label to the file input.
  const fileInputId = useId()

  const displayUrl = previewUrl ?? currentPhotoUrl
  const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-lg'

  async function handleFile(file: File) {
    setErr(null)
    if (!ACCEPT.split(',').includes(file.type)) {
      setErr('Please choose a JPEG, PNG, or WEBP image.')
      return
    }
    if (file.size > 25 * 1024 * 1024) {
      // sanity guard before we even try to decode; well above the server cap.
      setErr('That file is too large. Please pick something under 25 MB.')
      return
    }

    // Show preview immediately so the user sees feedback within a frame.
    const preview = URL.createObjectURL(file)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(preview)
    setUploading(true)

    try {
      const resized = await resizeToJpeg(file, maxEdgePx)

      // Deferred mode: parent collects the resized blob and uploads it later
      // (typically after the owning row has been created on form submit).
      // Preview stays so the user keeps seeing their pick.
      if (deferred) {
        onFilePicked?.(resized)
        return
      }

      const form = new FormData()
      form.append('file', new File([resized], 'photo.jpg', { type: 'image/jpeg' }))
      form.append('entity_type', entityType)
      if (entityType === 'player') {
        if (!entityId) throw new Error('entity_id_missing')
        form.append('entity_id', entityId)
      }
      const res = await apiFetch('/api/upload/photo', {
        method: 'POST',
        body: form,
      })
      const text = await res.text()
      const parsed = (text ? JSON.parse(text) : null) as
        | UploadResponse
        | { error?: string }
        | null
      if (!res.ok) {
        const msg = (parsed && 'error' in parsed && parsed.error) || `upload_failed_${res.status}`
        throw new Error(msg)
      }
      const data = parsed as UploadResponse
      // Swap preview → real server URL (cached 1 yr immutable so this is the
      // canonical URL going forward).
      URL.revokeObjectURL(preview)
      setPreviewUrl(null)
      onChange(data.photo_url)
    } catch (e) {
      // Roll back the preview so the avatar doesn't keep showing an
      // un-persisted image. The form input is reset so the user can retry
      // (picking the same file again triggers onChange).
      URL.revokeObjectURL(preview)
      setPreviewUrl(null)
      setErr(e instanceof Error ? e.message : 'upload_failed')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div>
      <label htmlFor={fileInputId} className="block text-sm font-medium text-hoop-text-2 mb-2">
        {label}
      </label>
      <div className="flex items-center gap-4">
        <div
          className={`relative w-20 h-20 ${shapeClass} bg-hoop-bg border border-hoop-border overflow-hidden flex-shrink-0 flex items-center justify-center`}
        >
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Photo preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-xs text-hoop-text-3">No photo</span>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-hoop-nav/70 text-hoop-nav-fg text-[10px] font-medium flex items-center justify-center">
              Uploading…
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 min-w-0">
          {/*
            C7 — the native file button rendered as flat text: file:bg matched
            the surrounding panel, there was no border, and no pointer cursor,
            so it read as a label rather than a control. Now styled as a real
            button, with cursor-pointer on both the input and its
            ::file-selector-button.

            Deliberately still a plain <input type="file">, NOT the
            hidden-input + styled-<label> trick: keyboard focus, Enter/Space
            activation, the htmlFor binding above and the native "No file
            chosen" state text all keep working for free. Auto-upload-on-pick
            and the maxEdgePx sizing are untouched.
          */}
          <input
            ref={inputRef}
            id={fileInputId}
            type="file"
            accept={ACCEPT}
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) void handleFile(f)
            }}
            className="text-sm text-hoop-text-2 cursor-pointer file:cursor-pointer file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border file:border-hoop-border file:text-sm file:font-medium file:bg-hoop-surface file:text-hoop-text hover:file:border-hoop-accent hover:file:text-hoop-accent disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <p className="text-xs text-hoop-text-3">
            JPEG, PNG, or WEBP.{' '}
            {deferred
              ? 'Uploaded when you submit the form.'
              : 'Auto-uploads on file pick.'}{' '}
            Resized to {maxEdgePx} px.
          </p>
          {err && (
            <p className="text-xs text-hoop-red">{err}</p>
          )}
        </div>
      </div>
    </div>
  )
}
